// @ts-ignore
import { findEligibleAchievementFromBasket } from '@winamax/missions-lib';
import { createSelector } from 'reselect';

import {
  selectBasketItemsEntity,
  selectBasketMode,
  selectBasketState,
} from '../../../selectors/basket/basketPrimary.selector';
import { selectBasketEnhancedItems, selectNbBasketItems } from '../../../selectors/basketItems.selector';
import { selectEntitiesAchievements } from '../../../selectors/entities/achievements/entitiesAchievementsPrimary.selector';
import { selectEntitiesState } from '../../../selectors/entities/entitiesPrimary.selector';
import { selectEntitiesMissions } from '../../../selectors/entities/missions/entitiesMissionsPrimary.selector';
import { selectRuntimeLicence } from '../../../selectors/runtime/runtimePrimary.selector';
import { type EntitiesMissions } from '../../../states/entities/missions';
import { BasketModes } from '../../../types/basket';
import { type Selector } from '../../../types/selectors';
import { AchievementId, type AchievementState } from '../types/state/achievements';
import { MissionId, type MissionState } from '../types/state/missions';
import { MissionOperationTypes } from '../types/state/missions';
import { UserAchievementStatuses, UserMissionStatuses } from '../types/state/userMissions';

import { selectRegisteredMissionIds } from './userMissionMissions';
import { selectUserMissionsState } from './userMissions';

const selectEligibleFilteredMissionsState: Selector<{ [index: number]: MissionState }> = createSelector(
  [selectEntitiesMissions, selectUserMissionsState, selectRegisteredMissionIds],
  (missions, userMissions, registeredMissionIds): EntitiesMissions => {
    const result: Record<number, MissionState> = {};

    for (const missionId of registeredMissionIds) {
      const userMissionStatus = userMissions[missionId]?.status;

      if (
        !userMissions ||
        (userMissionStatus !== UserMissionStatuses.WON && userMissionStatus !== UserMissionStatuses.LOST)
      ) {
        const mission = missions[missionId];
        const now = Date.now();

        if (
          mission?.operationType === MissionOperationTypes.MISSION &&
          mission?.startDate <= now &&
          (mission?.endDate ?? Infinity) > now
        ) {
          result[missionId] = mission;
        }
      }
    }

    return result;
  },
);

const selectEligibleFilteredAchievementsState: Selector<{ [index: number]: AchievementState }> = createSelector(
  [selectEligibleFilteredMissionsState, selectUserMissionsState, selectEntitiesAchievements],
  (missions, userMissions, achievements) => {
    const result: Record<number, AchievementState> = {};

    const missionIds = Object.keys(missions)
      .filter((k) => k)
      .map((k) => parseInt(k, 10));

    for (const missionId of missionIds) {
      const mission = missions[missionId];
      const userMission = userMissions[missionId];

      if (mission && mission.achievementIds.length) {
        const { achievementIds: missionAchievementIds } = mission;

        for (const achievementId of missionAchievementIds) {
          const userAchievementStatus = userMission?.achievements?.[achievementId]?.status;

          if (
            !userAchievementStatus ||
            userAchievementStatus === UserAchievementStatuses.OPEN ||
            userAchievementStatus === UserAchievementStatuses.PENDING
          ) {
            result[achievementId] = achievements[achievementId];
          }
        }
      }
    }

    return result;
  },
);

export const selectSimpleBasketItemEligibleMissionData: Selector<{
  achievementId: AchievementId;
  missionId: MissionId;
} | null> = createSelector(
  [
    selectBasketItemsEntity,
    selectBasketMode,
    selectEligibleFilteredMissionsState,
    selectEligibleFilteredAchievementsState,
    selectBasketState,
    selectEntitiesState,
    selectRuntimeLicence,
  ],
  (item, basketMode, missions, achievements, basket, entities, runtime) =>
    basketMode === BasketModes.SIMPLE
      ? findEligibleAchievementFromBasket({
          missions,
          basket,
          entities,
          runtime,
          achievements,
          item,
        })
      : null,
);

export const selectCombinedBasketEligibleMissionData: Selector<{
  achievementId: AchievementId;
  missionId: MissionId;
} | null> = createSelector(
  [
    selectEligibleFilteredMissionsState,
    selectBasketMode,
    selectEligibleFilteredAchievementsState,
    selectBasketState,
    selectEntitiesState,
    selectRuntimeLicence,
  ],
  (missions, basketMode, achievements, basket, entities, runtime) =>
    basketMode === BasketModes.COMBINED
      ? findEligibleAchievementFromBasket({
          missions,
          basket,
          entities,
          runtime,
          achievements,
        })
      : null,
);

export const selectFirstBasketItemEligibleMissionData: Selector<{
  achievementId: AchievementId;
  missionId: MissionId;
} | null> = createSelector(
  [
    selectEligibleFilteredMissionsState,
    selectEligibleFilteredAchievementsState,
    selectBasketState,
    selectEntitiesState,
    selectRuntimeLicence,
    selectBasketEnhancedItems,
  ],
  (missions, achievements, basket, entities, runtime, items) =>
    findEligibleAchievementFromBasket({
      missions,
      basket,
      entities,
      runtime,
      achievements,
      item: items[0],
    }),
);

export const selectSimpleBasketItemEligibleMission: Selector<MissionState | null> = createSelector(
  [selectSimpleBasketItemEligibleMissionData, selectEntitiesMissions],
  (basketItemEligibleMission, missions) =>
    basketItemEligibleMission?.missionId ? missions[basketItemEligibleMission.missionId] : null,
);

export const selectCombinedBasketEligibleMission: Selector<MissionState | null> = createSelector(
  [selectCombinedBasketEligibleMissionData, selectEntitiesMissions],
  (basketEligibleMission, missions) =>
    basketEligibleMission?.missionId ? missions[basketEligibleMission.missionId] : null,
);

export const selectFirstBasketItemEligibleMission: Selector<MissionState | null> = createSelector(
  [selectFirstBasketItemEligibleMissionData, selectEntitiesMissions],
  (basketFirstItemEligibleMission, missions) =>
    basketFirstItemEligibleMission?.missionId ? missions[basketFirstItemEligibleMission.missionId] : null,
);

const selectFirstBasketItemIsSingleMyMatch: Selector<boolean> = createSelector(
  [selectBasketEnhancedItems],
  (items) => items.length === 1 && items[0].betbuilder && !!items[0].selectionItems?.length,
);

export const selectBasketEligibleMission: Selector<MissionState | null> = createSelector(
  [
    selectCombinedBasketEligibleMission,
    selectSimpleBasketItemEligibleMission,
    selectFirstBasketItemEligibleMission,
    selectBasketMode,
    selectNbBasketItems,
    selectFirstBasketItemIsSingleMyMatch,
  ],
  (
    basketEligibleMission,
    basketItemEligibleMission,
    firstBasketItemsEligibleMission,
    basketMode,
    nbBasketItems,
    basketItemIsSingleMyMatch,
  ) => {
    if ((basketMode === BasketModes.SIMPLE && nbBasketItems === 1) || basketItemIsSingleMyMatch) {
      return firstBasketItemsEligibleMission;
    }

    return basketItemEligibleMission || basketEligibleMission;
  },
);
